import { Suspense, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogContent,
  LinearProgress,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Iconify } from 'src/components';
import { lazyWithRetries } from 'src/routes/lazyRetry';
import CallAudioPlayer from './CallAudioPlayer';
import CallKeyDetails from './CallKeyDetails';
import CallReviewHeader from './CallReviewHeader';
import CallSummarySection from './CallSummarySection';
import CallTranscriptReader from './CallTranscriptReader';
import useCallReviewData, { callReviewScope } from './useCallReviewData';
import { nothingToReviewLine, noRecordingLine, readExtracted, summaryStatusLine } from './callReviewModel';
import useDeskCallAi from './useDeskCallAi';

// The same stage screen the call pill opens, so a patient's call offers what
// their own Active Leads table offers. Loaded on first press: it pulls in every
// follow-up screen, and most calls never need one.
const StageScreen = lazyWithRetries(() => import('src/components/callPill/PillStageScreen'));

// Retained for existing importers; the original text no longer receives keyword colours.
export const HighlightedTranscript = ({ text }) => (
  <Typography sx={{ whiteSpace: 'pre-wrap', fontSize: 16, lineHeight: 1.75 }}>{text}</Typography>
);

const GenerationState = ({ state, label, available, onGenerate }) => (
  <Stack spacing={2} alignItems="flex-start">
    {state === 'loading' && (
      <Stack direction="row" spacing={1} alignItems="center">
        <CircularProgress size={18} />
        <Typography role="status">Generating {label}…</Typography>
      </Stack>
    )}
    {state === 'error' && (
      <Alert severity="error">
        Could not generate {label}. Check access and refresh the call list before trying again.
      </Alert>
    )}
    {state === 'uncertain' && (
      <Alert severity="warning">
        The result could not be confirmed. Refresh the call list to check for a saved {label} before trying again.
      </Alert>
    )}
    {available && state !== 'uncertain' && (
      <Button variant="outlined" disabled={state === 'loading'} onClick={onGenerate}>
        Generate {label}
      </Button>
    )}
  </Stack>
);

// Our transcript and summary are being made right now (CU-869fkj6eg). Show the
// shape of what is coming rather than an empty card or a bare spinner.
const WorkingSkeleton = ({ lines, label }) => (
  <Stack spacing={1} sx={{ width: '100%' }} aria-live="polite">
    {label && (
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    )}
    {Array.from({ length: lines }).map((_, index) => (
      <Skeleton key={index} variant="text" height={22} width={index === lines - 1 ? '62%' : '100%'} />
    ))}
  </Stack>
);

const SECTION_SX = { p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: 'none' };

// Body, top to bottom (CU-869fmd2x2): Key details → Summary (highlighted) → Recording or why
// not → Transcript (collapsible; one line when there is none).
function ReviewWorkspace({ callData, practiceId, onTranscriptReady, detailStatus, onRetryDetail, onRefreshDetail }) {
  const [selected, setSelected] = useState(callData?.primaryRecording?.id ?? callData?.recordings?.[0]?.id ?? '');
  const recordings = callData?.recordings || [];
  const recording = recordings.find((item) => item.id === selected) || callData?.primaryRecording || null;
  const scope = callReviewScope(practiceId, callData, recording);
  const data = useCallReviewData({ practiceId, call: callData, recording, onTranscriptReady });
  const extracted = useMemo(() => readExtracted(callData?.extracted), [callData?.extracted]);
  const hasTranscript = typeof data.transcript === 'string' && Boolean(data.transcript.trim());
  // Desk-phone calls Elevate did not summarise: ask for ours on open rather than
  // showing an empty panel with no way forward (CU-869fkj6eg).
  const ai = useDeskCallAi({ practiceId, call: callData, onRefresh: onRefreshDetail });
  const [transcriptOpen, setTranscriptOpen] = useState(!data.review);
  let transcriptLine = '\u00b7 No transcript for this call';
  if (ai.busy) transcriptLine = '\u00b7 Listening to the call\u2026';
  else if (ai.slow) transcriptLine = '\u00b7 Still working \u2014 reopen the call in a moment';
  // Nothing at all to review (e.g. a test call): one line instead of three empty cards.
  const empty = !data.review && !recording?.recordingUrl && !hasTranscript && !extracted
    && !data.canGenerateReview && !data.canGenerateTranscript;
  return (
    <DialogContent sx={{ p: { xs: 2, sm: 3 }, pt: { xs: 0, sm: 0 }, minHeight: 0 }}>
      <Stack spacing={2}>
        {/* The list carries no transcript or WhatsApp recordings: they come from the call's own read. */}
        {detailStatus === 'loading' && <LinearProgress aria-label="Loading call details" />}
        {detailStatus === 'error' && (
          <Alert severity="warning" action={onRetryDetail ? <Button color="inherit" size="small" onClick={onRetryDetail}>Retry</Button> : null}>
            The transcript and recordings could not be loaded.
          </Alert>
        )}
        {empty && (
          <Card sx={{ ...SECTION_SX, py: 1.5 }} data-testid="nothing-to-review">
            <Typography variant="body2" color="text.secondary">
              {nothingToReviewLine(callData).text}
            </Typography>
            {nothingToReviewLine(callData).detail && (
              <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
                {nothingToReviewLine(callData).detail}
              </Typography>
            )}
          </Card>
        )}
        {/* Read-only evidence. "Use in new lead" used to sit here and called the
            same handler as Create lead in the header, so the window offered one
            action under two names a few inches apart. */}
        {!empty && <CallKeyDetails extracted={extracted} />}
        {!empty && <CallSummarySection call={callData} review={data.review} extracted={extracted}>
          {ai.busy ? (
            <WorkingSkeleton lines={3} label={summaryStatusLine(callData, true)} />
          ) : (
            <Typography color="text.secondary">
              {data.canGenerateReview ? 'Create an AI-generated review of this call.' : summaryStatusLine(callData)}
            </Typography>
          )}
          <GenerationState state={data.reviewState} label="AI review" available={data.canGenerateReview} onGenerate={data.generateReview} />
        </CallSummarySection>}
        {!empty && <Card sx={SECTION_SX} component="section" aria-label="Recording">
          <Typography variant="subtitle1" sx={{ mb: recording?.recordingUrl ? 1.5 : 0.5 }}>
            Recording
          </Typography>
          {recordings.length > 1 && (
            <TextField select size="small" label="Recording" value={recording?.id ?? ''} onChange={(event) => setSelected(event.target.value)} sx={{ mb: 1.5 }}>
              {recordings.map((item, index) => (
                <MenuItem key={item.id} value={item.id}>
                  Recording {index + 1} — {item.status || 'Status unknown'}
                </MenuItem>
              ))}
            </TextField>
          )}
          {recording?.recordingUrl ? (
            <CallAudioPlayer key={scope} src={recording.recordingUrl} recordingState={callData?.recordingState} compact />
          ) : (
            <Typography variant="body2" color="text.secondary" data-testid="no-recording-reason">
              {noRecordingLine(callData)}
            </Typography>
          )}
        </Card>}
        {!empty && (hasTranscript ? (
          <Accordion
            expanded={transcriptOpen}
            onChange={(_, open) => setTranscriptOpen(open)}
            disableGutters
            sx={{ ...SECTION_SX, p: 0, '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<Iconify icon="eva:arrow-ios-downward-fill" />} aria-controls="transcript-panel" id="transcript-header">
              <Typography variant="subtitle1">Transcript</Typography>
            </AccordionSummary>
            <AccordionDetails id="transcript-panel" role="region" aria-label="Transcript">
              <CallTranscriptReader key={scope} text={data.transcript} extracted={extracted} speakers={callData?.transcriptSpeakers} compact />
            </AccordionDetails>
          </Accordion>
        ) : (
          <Card sx={{ ...SECTION_SX, py: 1.5 }} component="section" aria-label="Transcript">
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="subtitle1">Transcript</Typography>
              <Typography variant="body2" color="text.secondary">
                {transcriptLine}
              </Typography>
              <Box sx={{ ml: 'auto' }}>
                <GenerationState state={data.transcriptState} label="transcript" available={data.canGenerateTranscript} onGenerate={data.generateTranscript} />
              </Box>
            </Stack>
            {ai.busy && (
              <Box sx={{ mt: 1.5 }}>
                <WorkingSkeleton lines={4} label="" />
              </Box>
            )}
          </Card>
        ))}
      </Stack>
    </DialogContent>
  );
}

export default function RecordingModal({ open, onClose, callData, practiceId, onTranscriptReady, onFindContact, onCreateLead, onCallBack, detailStatus, onRetryDetail, onRefreshDetail, onOpenPatient, onStageDone }) {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));
  const scope = callReviewScope(practiceId, callData, null);
  // Which stage screen is open over this window, if any (callPillStage.stageOf).
  const [stage, setStage] = useState(null);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      fullScreen={mobile}
      maxWidth={false}
      aria-labelledby="call-review-title"
      PaperProps={{
        sx: {
          width: 960,
          maxWidth: mobile ? '100%' : 'calc(100vw - 32px)',
          maxHeight: mobile ? '100%' : '90dvh',
          m: mobile ? 0 : 2,
        },
      }}
    >
      <CallReviewHeader call={callData} practiceId={practiceId} onClose={onClose} onFindContact={onFindContact} onCreateLead={onCreateLead} onCallBack={onCallBack} onStageAction={setStage} />
      {stage && (
        <Suspense fallback={null}>
          <StageScreen
            practiceId={practiceId}
            leadId={stage.leadId}
            popup={stage.popup}
            // Booking or recording attendance moves the patient on, so the list
            // behind this window is stale the moment the screen closes.
            onClose={() => { setStage(null); onStageDone?.(); }}
            onNeedsLeadPage={() => { setStage(null); onOpenPatient?.(callData); }}
          />
        </Suspense>
      )}
      {open && callData && (
        <ReviewWorkspace
          key={scope}
          callData={callData}
          practiceId={practiceId}
          onTranscriptReady={onTranscriptReady}
          detailStatus={detailStatus}
          onRetryDetail={onRetryDetail}
          onRefreshDetail={onRefreshDetail}
        />
      )}
    </Dialog>
  );
}

const idType = PropTypes.oneOfType([PropTypes.string, PropTypes.number]);

ReviewWorkspace.propTypes = {
  callData: PropTypes.object.isRequired,
  practiceId: idType.isRequired,
  onTranscriptReady: PropTypes.func,
  // 'idle' | 'loading' | 'ready' | 'error' (useCallDetail)
  detailStatus: PropTypes.oneOf(['idle', 'loading', 'ready', 'error']),
  onRetryDetail: PropTypes.func,
  onRefreshDetail: PropTypes.func,
};
ReviewWorkspace.defaultProps = { onTranscriptReady: undefined, detailStatus: 'idle', onRetryDetail: undefined, onRefreshDetail: undefined };

RecordingModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  callData: PropTypes.object,
  practiceId: idType,
  onTranscriptReady: PropTypes.func,
  onFindContact: PropTypes.func,
  onCreateLead: PropTypes.func,
  onCallBack: PropTypes.func,
  detailStatus: PropTypes.oneOf(['idle', 'loading', 'ready', 'error']),
  onRetryDetail: PropTypes.func,
  onRefreshDetail: PropTypes.func,
  onOpenPatient: PropTypes.func,
  onStageDone: PropTypes.func,
};
RecordingModal.defaultProps = {
  open: false, callData: null, practiceId: undefined, onTranscriptReady: undefined, onFindContact: undefined, onCreateLead: undefined, onCallBack: undefined, detailStatus: 'idle', onRetryDetail: undefined, onRefreshDetail: undefined, onOpenPatient: undefined, onStageDone: undefined,
};
WorkingSkeleton.propTypes = { lines: PropTypes.number, label: PropTypes.string };
WorkingSkeleton.defaultProps = { lines: 3, label: '' };

HighlightedTranscript.propTypes = { text: PropTypes.string };
HighlightedTranscript.defaultProps = { text: '' };
GenerationState.propTypes = { state: PropTypes.string, label: PropTypes.string, available: PropTypes.bool, onGenerate: PropTypes.func };
GenerationState.defaultProps = { state: undefined, label: '', available: false, onGenerate: undefined };
